/**
 * useEnrichmentsStore - Hook for enrichment management with immediate API sync.
 *
 * All operations are immediately pushed to the server.
 * No local state - the server is the single source of truth.
 */

import { createResourceHook } from "../use-resource-store";
import {
  useEnrichments,
  useCreateEnrichment,
  useDeleteEnrichment,
} from "@/api/enrichment";
import { Enrichment } from "@/models/enrichment";
import { useApiMutation } from "@/hooks/use-api";

// Enrichment with required id for BaseResource constraint
interface EnrichmentWithId extends Enrichment {
  id: string;
}

/**
 * Hook for updating enrichments (not exported from API, create locally).
 */
const useUpdateEnrichment = () =>
  useApiMutation<Enrichment, Partial<Enrichment>, { id: string }>({
    url: "enrichments/{id}",
    method: "PATCH",
  });

/**
 * Hook for enrichment management with immediate API sync.
 *
 * @example
 * ```tsx
 * function EnrichmentList() {
 *   const {
 *     resources: enrichments,
 *     create,
 *     update,
 *     remove,
 *     isLoading,
 *     isUpdating,
 *   } = useEnrichmentsStore();
 *
 *   const handleUpdate = async (id: string, name: string) => {
 *     await update(id, { name });
 *   };
 *
 *   return (
 *     <div>
 *       {enrichments.map(e => (
 *         <EnrichmentItem key={e.id} enrichment={e} onUpdate={handleUpdate} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useEnrichmentsStore = createResourceHook<
  EnrichmentWithId,
  Partial<Enrichment>
>({
  resourceName: "enrichment",
  resourcePath: "enrichments",

  useQueryHook: () => {
    const { data, isLoading, error, refetch } = useEnrichments();
    return {
      data: (data as EnrichmentWithId[] | undefined) ?? undefined,
      isLoading,
      error: error as Error | null,
      refetch,
    };
  },

  useCreateMutation: () => {
    const mutation = useCreateEnrichment();
    return {
      mutateAsync: async (data: Partial<Enrichment>) => {
        const result = await mutation.mutateAsync(data);
        return result as EnrichmentWithId;
      },
    };
  },

  useUpdateMutation: () => {
    const mutation = useUpdateEnrichment();
    return {
      mutateAsync: async (
        data: { uriParams: { id: string } } & Record<string, unknown>
      ) => {
        const { uriParams, ...rest } = data;
        const result = await mutation.mutateAsync({
          uriParams,
          ...rest,
        } as Parameters<typeof mutation.mutateAsync>[0]);
        return result as EnrichmentWithId;
      },
    };
  },

  useDeleteMutation: () => {
    const mutation = useDeleteEnrichment();
    return {
      mutateAsync: async (data: { uriParams: { id: string } }) => {
        await mutation.mutateAsync(data);
      },
    };
  },
});

export default useEnrichmentsStore;
